import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BarChart3, Info, LineChart as LineChartIcon, RefreshCw } from 'lucide-react'
import Select, { type SingleValue, type StylesConfig } from 'react-select'
import { useSystemsContext } from '../hooks/useSystemsContext'
import { useDevicesContext } from "../hooks/useDevicesContext"
import { useAuthContext } from '../hooks/useAuthContext'
import LineChart from "../components/Charts/LineChart";
import { fetchDevices, getDevicesInSystem } from "../helper/deviceHelper";
import { System, SystemDevice } from "../context/SystemContext";
import { DeviceListItem } from "../types";
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { hydroReactSelectStyles } from '@/lib/hydroReactSelectStyles'

type AnalyticsOption = { value: string; label: string }

const analyticsSelectStyles = hydroReactSelectStyles as StylesConfig<AnalyticsOption, false>

const DISPLAY_OPTIONS: AnalyticsOption[] = [
    { value: 'all', label: 'All sensors' },
    { value: 'systems', label: 'Systems' },
]

const TIME_RANGE_OPTIONS: AnalyticsOption[] = [
    { value: 'hour', label: 'Past Hour' },
    { value: 'day', label: 'Past Day' },
    { value: 'week', label: 'Past Week' },
    { value: 'month', label: 'Past Month' },
]

/** Ordered keys for chart buckets — order matches previous `sensorSettings` / LineChart layout. */
const SENSOR_BUCKET_KEYS = [
    'temperature',
    'humidity',
    'ph',
    'ec',
    'tds',
    'soilMoisture',
    'waterFlow',
] as const

type SensorBucketKey = (typeof SENSOR_BUCKET_KEYS)[number]

type ChartBuckets = Record<SensorBucketKey, DeviceListItem[]>

const SENSOR_BUCKET_CONFIG: Record<
    SensorBucketKey,
    { readings: string; matches: (deviceType: string) => boolean }
> = {
    temperature: {
        readings: 'Temperature Readings',
        matches: (dt) => dt.includes('Temperature'),
    },
    humidity: {
        readings: 'Humidity Readings',
        matches: (dt) => dt.includes('Humidity'),
    },
    ph: {
        readings: 'pH Readings',
        matches: (dt) => dt.includes('pH'),
    },
    ec: {
        readings: 'Electrical Conductivity Readings',
        matches: (dt) => dt.includes('EC'),
    },
    tds: {
        readings: 'TDS Readings',
        matches: (dt) => dt.includes('TDS'),
    },
    soilMoisture: {
        readings: 'Soil Moisture Readings',
        matches: (dt) => dt.includes('Moisture'),
    },
    waterFlow: {
        readings: 'Water Flow Sensor Readings',
        matches: (dt) => dt.includes('Water Flow'),
    },
}

function emptyChartBuckets(): ChartBuckets {
    return {
        temperature: [],
        humidity: [],
        ph: [],
        ec: [],
        tds: [],
        soilMoisture: [],
        waterFlow: [],
    }
}

const menuPortalTarget =
    typeof document !== 'undefined' ? document.documentElement : undefined

const Records = () => {
    const [currentDisplay, setCurrentDisplay] = useState<string>('')
    const [currentEnvironment, setCurrentEnvironment] = useState<System | null>(null)
    const [currentSensor, setCurrentSensor] = useState<SystemDevice | null>(null)
    const [chartBuckets, setChartBuckets] = useState<ChartBuckets>(emptyChartBuckets)
    const { systems } = useSystemsContext() 
    const { devices, dispatch: deviceDispatch } = useDevicesContext()
    const [finishedDisplay, setFinishedDisplay] = useState<boolean>(false)
    const [timeInterval, setTimeInterval] = useState<string>('week')
    const { user } = useAuthContext()

    const sensorOptions = useMemo((): AnalyticsOption[] => {
        if (!devices) return []
        const out: AnalyticsOption[] = []
        for (const device of devices) {
            if (!device.device_type.includes('Outlet') && !device.device_type.includes('Pump')) {
                out.push({ value: JSON.stringify(device), label: device.name })
            }
        }
        return out
    }, [devices])

    const systemOptions = useMemo((): AnalyticsOption[] => {
        if (!systems) return []
        return systems
            .filter((system): system is System => system != null && typeof system === 'object' && '_id' in system)
            .map((system) => ({ value: JSON.stringify(system), label: system.name }))
    }, [systems])

    const displaySelectValue = useMemo(() => {
        if (!currentDisplay) return null
        return DISPLAY_OPTIONS.find((o) => o.value === currentDisplay) ?? null
    }, [currentDisplay])

    const sensorSelectValue = useMemo(() => {
        if (!currentSensor) return null
        return (
            sensorOptions.find((o) => {
                try {
                    const d = JSON.parse(o.value) as SystemDevice
                    return d._id === currentSensor._id
                } catch {
                    return false
                }
            }) ?? null
        )
    }, [currentSensor, sensorOptions])

    const systemSelectValue = useMemo(() => {
        if (!currentEnvironment || typeof currentEnvironment !== 'object' || !('_id' in currentEnvironment)) {
            return null
        }
        const id = (currentEnvironment as System)._id
        return (
            systemOptions.find((o) => {
                try {
                    const s = JSON.parse(o.value) as System
                    return s._id === id
                } catch {
                    return false
                }
            }) ?? null
        )
    }, [currentEnvironment, systemOptions])

    const timeRangeSelectValue = useMemo(
        () => TIME_RANGE_OPTIONS.find((o) => o.value === timeInterval) ?? null,
        [timeInterval]
    )

    const refreshDeviceList = useCallback(async () => {
        if (!user) return
        await fetchDevices(user, deviceDispatch)
    }, [user, deviceDispatch])

    const handleToolbarRefresh = () => {
        void refreshDeviceList()
    }

    const clearSensorVariables = useCallback(() => {
        setFinishedDisplay(false)
        setChartBuckets(emptyChartBuckets())
    }, [])
    
    useEffect(() => {
        clearSensorVariables()
        void refreshDeviceList()
    }, [refreshDeviceList, clearSensorVariables])

    /**
     * Build chart device lists from API-shaped devices. Systems no longer embed `devices` on the
     * system document — assignments live on each device (`system_id`).
     */
    const applyChartDataFromDevices = useCallback((deviceRows: SystemDevice[]) => {
        const next = emptyChartBuckets()
        for (const device of deviceRows) {
            const dt = device.device_type ?? ''
            const item: DeviceListItem = { device_id: device.device_id, name: device.name }
            for (const key of SENSOR_BUCKET_KEYS) {
                if (SENSOR_BUCKET_CONFIG[key].matches(dt)) {
                    next[key].push(item)
                }
            }
        }
        setChartBuckets(next)
        setFinishedDisplay(true)
    }, [])

    useEffect(() => {
        if (currentSensor === null) return
        clearSensorVariables()
        setCurrentEnvironment(null)
        applyChartDataFromDevices([currentSensor])
    }, [currentSensor, applyChartDataFromDevices, clearSensorVariables])

    useEffect(() => {
        if (
            currentEnvironment === null ||
            typeof currentEnvironment !== 'object' ||
            !('_id' in currentEnvironment) ||
            !user
        ) {
            return
        }

        let cancelled = false
        const systemId = (currentEnvironment as System)._id

        const run = async () => {
            clearSensorVariables()
            setCurrentSensor(null)
            const raw = await getDevicesInSystem(user, systemId)
            if (cancelled) return
            const list = Array.isArray(raw) ? raw : []
            const chartable = list.filter(
                (d: { device_type?: string }) =>
                    Boolean(d?.device_type) &&
                    !String(d.device_type).includes('Outlet') &&
                    !String(d.device_type).includes('Pump')
            ) as SystemDevice[]
            applyChartDataFromDevices(chartable)
        }

        void run()
        return () => {
            cancelled = true
        }
    }, [currentEnvironment, user, applyChartDataFromDevices, clearSensorVariables])

    const sensorSettings = useMemo(
        () =>
            SENSOR_BUCKET_KEYS.map((key) => {
                const deviceList = chartBuckets[key]
                return {
                    exists: deviceList.length > 0,
                    deviceList,
                    readings: SENSOR_BUCKET_CONFIG[key].readings,
                }
            }),
        [chartBuckets]
    )

    const timeRangeLabel = timeRangeSelectValue?.label ?? timeInterval

    const selectionReady =
        (currentDisplay === 'all' && currentSensor !== null) ||
        (currentDisplay === 'systems' && currentEnvironment !== null)

    const hasAnyChart = finishedDisplay && sensorSettings.some((s) => s.exists)

    const contextHeadline = useMemo(() => {
        if (!currentDisplay) return null
        if (currentDisplay === 'all' && currentSensor) return currentSensor.name
        if (currentDisplay === 'systems' && currentEnvironment) return currentEnvironment.name
        return null
    }, [currentDisplay, currentSensor, currentEnvironment])

    return (
        <>
            <header
                className={cn(
                    'device-toolbar flex w-full min-w-0 shrink-0 flex-col gap-4 overflow-visible border-b border-zinc-800/90 bg-zinc-900/95 px-3 py-3.5 shadow-sm backdrop-blur-sm supports-[backdrop-filter]:bg-zinc-900/80 sm:px-5 sm:py-4'
                )}
                aria-label="Analytics toolbar"
            >
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div className="flex items-center justify-center gap-2 sm:justify-start">
                        <BarChart3 className="size-5 shrink-0 text-emerald-400/90" aria-hidden />
                        <span className="text-center text-xs font-semibold uppercase tracking-wide text-zinc-400 sm:text-left sm:text-sm">
                            Analytics
                        </span>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleToolbarRefresh}
                        className="h-10 min-h-10 w-full min-w-0 justify-center gap-2 border-zinc-600 bg-zinc-950/60 px-3 text-sm text-zinc-100 shadow-sm hover:bg-zinc-800 hover:text-white sm:w-auto sm:shrink-0 sm:px-4"
                    >
                        <RefreshCw className="size-4 shrink-0" aria-hidden />
                        <span className="truncate">Refresh data</span>
                    </Button>
                </div>

                <div className="flex w-full flex-col gap-4 border-t border-zinc-800/80 pt-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-4 sm:gap-y-3 sm:border-t-0 sm:pt-0">
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[12rem] sm:max-w-xs sm:flex-initial">
                        <Label htmlFor="analytics-display-input" className="text-xs font-medium text-zinc-400">
                            Display
                        </Label>
                        <div className="relative z-10 min-h-[44px] w-full min-w-0">
                            <Select<AnalyticsOption, false>
                                instanceId="analytics-display"
                                inputId="analytics-display-input"
                                classNamePrefix="analytics-display"
                                className="w-full min-w-0"
                                options={DISPLAY_OPTIONS}
                                value={displaySelectValue}
                                placeholder="Select display"
                                onChange={(opt: SingleValue<AnalyticsOption>) => {
                                    if (!opt) return
                                    clearSensorVariables()
                                    setCurrentDisplay(opt.value)
                                    setCurrentSensor(null)
                                    setCurrentEnvironment(null)
                                }}
                                styles={analyticsSelectStyles}
                                menuPortalTarget={menuPortalTarget}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                isSearchable={false}
                                blurInputOnSelect
                                aria-label="Display mode"
                            />
                        </div>
                    </div>

                    {currentDisplay === 'all' && (
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[12rem] sm:max-w-xs sm:flex-initial">
                            <Label htmlFor="analytics-sensor-input" className="text-xs font-medium text-zinc-400">
                                Sensor
                            </Label>
                            <div className="relative z-10 min-h-[44px] w-full min-w-0">
                                <Select<AnalyticsOption, false>
                                    instanceId="analytics-sensor"
                                    inputId="analytics-sensor-input"
                                    classNamePrefix="analytics-sensor"
                                    className="w-full min-w-0"
                                    options={sensorOptions}
                                    value={sensorSelectValue}
                                    placeholder="Select sensor"
                                    onChange={(opt: SingleValue<AnalyticsOption>) => {
                                        if (!opt) return
                                        setCurrentEnvironment(null)
                                        clearSensorVariables()
                                        setCurrentSensor(JSON.parse(opt.value) as SystemDevice)
                                    }}
                                    styles={analyticsSelectStyles}
                                    menuPortalTarget={menuPortalTarget}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    isSearchable={sensorOptions.length > 6}
                                    blurInputOnSelect
                                    aria-label="Sensor"
                                />
                            </div>
                        </div>
                    )}

                    {currentDisplay === 'systems' && (
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[12rem] sm:max-w-xs sm:flex-initial">
                            <Label htmlFor="analytics-system-input" className="text-xs font-medium text-zinc-400">
                                System
                            </Label>
                            <div className="relative z-10 min-h-[44px] w-full min-w-0">
                                <Select<AnalyticsOption, false>
                                    instanceId="analytics-system"
                                    inputId="analytics-system-input"
                                    classNamePrefix="analytics-system"
                                    className="w-full min-w-0"
                                    options={systemOptions}
                                    value={systemSelectValue}
                                    placeholder="Select system"
                                    onChange={(opt: SingleValue<AnalyticsOption>) => {
                                        if (!opt) return
                                        setCurrentSensor(null)
                                        clearSensorVariables()
                                        setCurrentEnvironment(JSON.parse(opt.value) as System)
                                    }}
                                    styles={analyticsSelectStyles}
                                    menuPortalTarget={menuPortalTarget}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    isSearchable={systemOptions.length > 6}
                                    blurInputOnSelect
                                    aria-label="System"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[12rem] sm:max-w-xs sm:flex-initial">
                        <Label htmlFor="analytics-time-range-input" className="text-xs font-medium text-zinc-400">
                            Time range
                        </Label>
                        <div className="relative z-10 min-h-[44px] w-full min-w-0">
                            <Select<AnalyticsOption, false>
                                instanceId="analytics-time-range"
                                inputId="analytics-time-range-input"
                                classNamePrefix="analytics-time-range"
                                className="w-full min-w-0"
                                options={TIME_RANGE_OPTIONS}
                                value={timeRangeSelectValue}
                                placeholder="Select time period"
                                onChange={(opt: SingleValue<AnalyticsOption>) => {
                                    if (!opt) return
                                    setTimeInterval(opt.value)
                                }}
                                styles={analyticsSelectStyles}
                                menuPortalTarget={menuPortalTarget}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                isSearchable={false}
                                blurInputOnSelect
                                aria-label="Time range"
                            />
                        </div>
                    </div>
                </div>
            </header>

            <div className="mx-auto w-full max-w-6xl space-y-4 px-3 pb-12 pt-4 sm:px-5">
                {!currentDisplay ? (
                    <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                        <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                                <Info className="size-5" aria-hidden />
                            </div>
                            <div className="min-w-0 space-y-1">
                                <CardTitle className="text-base text-zinc-50">Get started with analytics</CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Choose <strong className="font-medium text-zinc-300">All sensors</strong> to graph one
                                    device at a time, or <strong className="font-medium text-zinc-300">Systems</strong> to
                                    see every chartable sensor in a grow environment. Then pick a{' '}
                                    <strong className="font-medium text-zinc-300">time range</strong>. Pumps and smart
                                    outlets are hidden here—only sensors with history (temp, pH, EC, humidity, etc.)
                                    appear.
                                </CardDescription>
                            </div>
                        </CardHeader>
                    </Card>
                ) : null}

                {currentDisplay === 'all' && !currentSensor && sensorOptions.length > 0 ? (
                    <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                        <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
                            <LineChartIcon className="size-5 shrink-0 text-emerald-400/90" aria-hidden />
                            <div className="min-w-0 space-y-1">
                                <CardTitle className="text-base text-zinc-50">Select a sensor</CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Pick a sensor from the dropdown above. Charts appear for each measurement type that
                                    device supports (for example temperature and humidity on one device).
                                </CardDescription>
                            </div>
                        </CardHeader>
                    </Card>
                ) : null}

                {currentDisplay === 'systems' && !currentEnvironment && systemOptions.length > 0 ? (
                    <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                        <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
                            <LineChartIcon className="size-5 shrink-0 text-emerald-400/90" aria-hidden />
                            <div className="min-w-0 space-y-1">
                                <CardTitle className="text-base text-zinc-50">Select a system</CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Choose a grow environment to load trends for all chartable sensors assigned to that
                                    system.
                                </CardDescription>
                            </div>
                        </CardHeader>
                    </Card>
                ) : null}

                {currentDisplay && sensorOptions.length === 0 && currentDisplay === 'all' ? (
                    <Card className="border-amber-900/40 bg-amber-950/20 text-amber-50 shadow-lg ring-1 ring-amber-800/30">
                        <CardContent className="pt-6">
                            <p className="text-sm leading-relaxed text-amber-100/90">
                                No standalone sensors are available yet. Add devices in{' '}
                                <strong className="font-medium">Device manager</strong>, or try{' '}
                                <strong className="font-medium">Systems</strong> if your sensors are grouped there.
                            </p>
                        </CardContent>
                    </Card>
                ) : null}

                {currentDisplay === 'systems' && systemOptions.length === 0 ? (
                    <Card className="border-amber-900/40 bg-amber-950/20 text-amber-50 shadow-lg ring-1 ring-amber-800/30">
                        <CardContent className="pt-6">
                            <p className="text-sm leading-relaxed text-amber-100/90">
                                You don&apos;t have any systems yet. Create one from the home <strong className="font-medium">Systems</strong>{' '}
                                page, assign sensors, then return here.
                            </p>
                        </CardContent>
                    </Card>
                ) : null}

                {selectionReady ? (
                    <Card className="border-zinc-700 bg-zinc-900/60 text-zinc-100 shadow-md ring-1 ring-zinc-800/50">
                        <CardContent className="flex flex-col gap-2 pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Current view</p>
                                <p className="truncate text-base font-semibold text-zinc-50">
                                    {contextHeadline ?? '—'}
                                </p>
                                <p className="mt-0.5 text-sm text-zinc-400">
                                    Time range: <span className="text-zinc-200">{timeRangeLabel}</span>
                                    {currentDisplay === 'all' ? (
                                        <span className="text-zinc-500"> · Single sensor</span>
                                    ) : (
                                        <span className="text-zinc-500"> · All chartable sensors in system</span>
                                    )}
                                </p>
                            </div>
                            <p className="max-w-md text-xs leading-relaxed text-zinc-500">
                                Charts load below. Use <strong className="font-medium text-zinc-400">Refresh data</strong>{' '}
                                if devices were just added or reassigned.
                            </p>
                        </CardContent>
                    </Card>
                ) : null}

                {finishedDisplay && !hasAnyChart && selectionReady ? (
                    <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                        <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
                            <Info className="size-5 shrink-0 text-zinc-400" aria-hidden />
                            <div className="min-w-0 space-y-1">
                                <CardTitle className="text-base text-zinc-50">Nothing to chart for this selection</CardTitle>
                                <CardDescription className="text-zinc-400">
                                    This choice only includes pumps, outlets, or devices we don&apos;t graph here. Add or
                                    select an environment with temperature, humidity, pH, EC, TDS, moisture, or flow
                                    sensors to see trends.
                                </CardDescription>
                            </div>
                        </CardHeader>
                    </Card>
                ) : null}

                <div className="flex flex-col items-stretch gap-2">
                    {sensorSettings.map((sensor) =>
                        sensor.exists && finishedDisplay ? (
                            <LineChart
                                key={sensor.readings}
                                device_list={sensor.deviceList}
                                device_type={sensor.readings}
                                timeInterval={timeInterval}
                            />
                        ) : null
                    )}
                </div>
            </div>
        </>
    )
}

export default Records;    


